import React, { Component } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Tooltip,
  Select,
  Input,
  Row,
  Col,
  Statistic,
  Spin,
  Alert,
  Popconfirm,
  notification,
  Radio,
  Progress,
} from "antd";
import {
  SyncOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  SearchOutlined,
  DeleteOutlined,
  LinkOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  ApiOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import {
  getSystemMetricsAPI,
  resetSystemMetricsAPI,
} from "../../Graphs/Setting/systemMetricsAPI";
import { WEBURL } from "../../constant/comman";
import "./QueryPerformanceTab.scss";

const { Option } = Select;

export class QueryPerformanceTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      refreshing: false,
      resetting: false,
      error: null,
      data: null,
      activeView: "routes", // "routes" | "db"
      sortBy: "requests",
      minRequests: 0,
      searchQuery: "",
      methodFilter: "ALL",
      opFilter: "ALL",
      autoRefreshSec: 0,
      lastUpdated: null,
      copiedKey: null,
    };
    this.timer = null;
  }

  componentDidMount() {
    this.loadMetrics();
  }

  componentWillUnmount() {
    this.clearAutoRefresh();
  }

  clearAutoRefresh = () => {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  };

  setupAutoRefresh = (seconds) => {
    this.clearAutoRefresh();
    if (seconds > 0) {
      this.timer = setInterval(() => {
        this.loadMetrics(true);
      }, seconds * 1000);
    }
  };

  handleAutoRefreshChange = (value) => {
    const sec = parseInt(value, 10) || 0;
    this.setState({ autoRefreshSec: sec });
    this.setupAutoRefresh(sec);
  };

  loadMetrics = async (silent = false) => {
    const { authToken } = this.props;
    const { sortBy, minRequests } = this.state;

    if (!silent) {
      this.setState({ loading: true, error: null });
    } else {
      this.setState({ refreshing: true });
    }

    try {
      const response = await getSystemMetricsAPI(authToken, {
        sort: sortBy,
        minRequests,
        limit: 250,
      });

      if (response && response.success && response.data) {
        this.setState({
          data: response.data,
          loading: false,
          refreshing: false,
          error: null,
          lastUpdated: new Date(),
        });
      } else {
        const errorMsg =
          (response && response.message) || "Failed to load query metrics";
        this.setState({
          loading: false,
          refreshing: false,
          error: errorMsg,
        });
      }
    } catch (err) {
      console.error("Error loading system metrics:", err);
      this.setState({
        loading: false,
        refreshing: false,
        error:
          err.message ||
          "Could not connect to query metrics API. Please verify server status.",
      });
    }
  };

  handleReset = async () => {
    const { authToken } = this.props;
    this.setState({ resetting: true });
    try {
      const res = await resetSystemMetricsAPI(authToken);
      if (res && res.success) {
        notification.success({
          message: "Counters Reset",
          description: "All query counters and timing statistics have been reset.",
        });
        await this.loadMetrics();
      } else {
        notification.error({
          message: "Reset Failed",
          description: (res && res.message) || "Could not reset metrics.",
        });
      }
    } catch (err) {
      notification.error({
        message: "Reset Error",
        description: err.message || "An unexpected error occurred.",
      });
    } finally {
      this.setState({ resetting: false });
    }
  };

  handleSortChange = (val) => {
    this.setState({ sortBy: val }, () => {
      this.loadMetrics();
    });
  };

  openStandaloneDashboard = () => {
    const { authToken } = this.props;
    const token =
      authToken ||
      localStorage.getItem("token") ||
      localStorage.getItem("admin_token") ||
      "";
    const base = WEBURL.endsWith("/") ? WEBURL : WEBURL + "/";
    const url = `${base}v1/system/qps-metrics?html=true&access_token=${encodeURIComponent(
      token
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  copyToClipboard = (text, key) => {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
      this.setState({ copiedKey: key });
      setTimeout(() => this.setState({ copiedKey: null }), 2000);
    }
  };

  getMethodColor = (method) => {
    switch (String(method).toUpperCase()) {
      case "GET":
        return "#10b981";
      case "POST":
        return "#2563eb";
      case "PUT":
        return "#f59e0b";
      case "DELETE":
        return "#ef4444";
      default:
        return "#64748b";
    }
  };

  getOpColor = (op) => {
    switch (String(op).toUpperCase()) {
      case "SELECT":
        return "#2563eb";
      case "INSERT":
        return "#10b981";
      case "UPDATE":
        return "#f59e0b";
      case "DELETE":
        return "#ef4444";
      case "CALL":
        return "#8b5cf6";
      default:
        return "#475569";
    }
  };

  getLatencyColor = (ms) => {
    if (ms < 20) return "#10b981";
    if (ms < 80) return "#f59e0b";
    return "#ef4444";
  };

  getFilteredRoutes = () => {
    const { data, searchQuery, methodFilter } = this.state;
    if (!data || !Array.isArray(data.routes)) return [];

    const query = searchQuery.trim().toLowerCase();
    return data.routes.filter((route) => {
      if (methodFilter !== "ALL" && route.method !== methodFilter) {
        return false;
      }
      if (!query) return true;
      const routeText = `${route.method} ${route.route || ""}`.toLowerCase();
      return routeText.includes(query);
    });
  };

  getFilteredDbQueries = () => {
    const { data, searchQuery, opFilter } = this.state;
    if (!data || !Array.isArray(data.db_queries)) return [];

    const query = searchQuery.trim().toLowerCase();
    return data.db_queries.filter((q) => {
      if (opFilter !== "ALL" && q.operation !== opFilter) {
        return false;
      }
      if (!query) return true;
      const text = `${q.operation} ${q.table || ""} ${q.query || ""}`.toLowerCase();
      return text.includes(query);
    });
  };

  render() {
    const {
      loading,
      refreshing,
      resetting,
      error,
      data,
      activeView,
      searchQuery,
      methodFilter,
      opFilter,
      sortBy,
      autoRefreshSec,
      lastUpdated,
      copiedKey,
    } = this.state;

    const system = (data && data.system) || {};
    const filteredRoutes = this.getFilteredRoutes();
    const filteredDbQueries = this.getFilteredDbQueries();

    // Simplified API Routes Columns
    const routeColumns = [
      {
        title: "Method",
        dataIndex: "method",
        key: "method",
        width: 85,
        render: (method) => (
          <Tag
            color={this.getMethodColor(method)}
            style={{ fontWeight: 700, borderRadius: 4, minWidth: 50, textAlign: "center" }}
          >
            {method}
          </Tag>
        ),
      },
      {
        title: "API Endpoint Route",
        dataIndex: "route",
        key: "route",
        render: (route, record) => (
          <div style={{ wordBreak: "break-all" }}>
            <span
              style={{
                fontFamily: "monospace",
                fontWeight: 600,
                color: "#0f172a",
                fontSize: "13px",
              }}
            >
              {route || record.key}
            </span>
          </div>
        ),
      },
      {
        title: "Times Run (Calls)",
        dataIndex: "totalRequests",
        key: "totalRequests",
        width: 140,
        align: "right",
        sorter: (a, b) => (a.totalRequests || 0) - (b.totalRequests || 0),
        render: (hits, record) => (
          <div>
            <div style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>
              {(hits || 0).toLocaleString()}
            </div>
            {(record.liveQps > 0 || record.currentQps > 0) && (
              <div style={{ fontSize: "11px", color: "#2563eb" }}>
                {record.liveQps || record.currentQps} /s
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Avg Time Taken",
        dataIndex: "avgLatencyMs",
        key: "avgLatencyMs",
        width: 130,
        align: "right",
        sorter: (a, b) => (a.avgLatencyMs || 0) - (b.avgLatencyMs || 0),
        render: (ms) => (
          <Tag
            style={{
              backgroundColor: "transparent",
              borderColor: this.getLatencyColor(ms || 0),
              color: this.getLatencyColor(ms || 0),
              fontWeight: 700,
              fontSize: "13px",
              padding: "2px 8px",
            }}
          >
            {ms !== undefined ? `${ms} ms` : "-"}
          </Tag>
        ),
      },
      {
        title: "DB Queries / Call",
        dataIndex: "dbAvgQueriesPerReq",
        key: "dbAvgQueriesPerReq",
        width: 130,
        align: "right",
        sorter: (a, b) => (a.dbAvgQueriesPerReq || 0) - (b.dbAvgQueriesPerReq || 0),
        render: (val, r) => (
          <Tooltip title={`Total DB queries: ${(r.dbQueryCount || 0).toLocaleString()}, DB time: ${(r.dbTotalTimeMs || 0).toLocaleString()} ms`}>
            <span style={{ fontWeight: 600, color: val > 5 ? "#dc2626" : "#334155" }}>
              {val !== undefined ? `${val} /req` : "-"}
            </span>
          </Tooltip>
        ),
      },
    ];

    // Simplified Database Queries Columns
    const dbColumns = [
      {
        title: "Operation",
        dataIndex: "operation",
        key: "operation",
        width: 95,
        render: (op) => (
          <Tag
            color={this.getOpColor(op)}
            style={{ fontWeight: 700, borderRadius: 4, minWidth: 60, textAlign: "center" }}
          >
            {op}
          </Tag>
        ),
      },
      {
        title: "Table",
        dataIndex: "table",
        key: "table",
        width: 130,
        render: (tbl) => (
          <Tag color="geekblue" style={{ fontWeight: 600 }}>
            {tbl || "raw"}
          </Tag>
        ),
      },
      {
        title: "SQL Query Pattern",
        dataIndex: "query",
        key: "query",
        render: (query, record) => (
          <div style={{ wordBreak: "break-all" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <span
                style={{
                  fontFamily: "monospace",
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "12px",
                  lineHeight: "1.4",
                }}
              >
                {query}
              </span>
              <Button
                type="text"
                size="small"
                icon={copiedKey === record.key ? <CheckCircleOutlined style={{ color: "#10b981" }} /> : <CopyOutlined />}
                onClick={() => this.copyToClipboard(query, record.key)}
                style={{ padding: "0 4px", height: "auto" }}
                title="Copy SQL pattern"
              />
            </div>
          </div>
        ),
      },
      {
        title: "Times Run",
        dataIndex: "totalExecutions",
        key: "totalExecutions",
        width: 120,
        align: "right",
        sorter: (a, b) => (a.totalExecutions || 0) - (b.totalExecutions || 0),
        render: (hits, record) => (
          <div>
            <div style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>
              {(hits || 0).toLocaleString()}
            </div>
            {(record.liveQps > 0) && (
              <div style={{ fontSize: "11px", color: "#2563eb" }}>
                {record.liveQps} /s
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Avg Time Taken",
        dataIndex: "avgTimeMs",
        key: "avgTimeMs",
        width: 125,
        align: "right",
        sorter: (a, b) => (a.avgTimeMs || 0) - (b.avgTimeMs || 0),
        render: (ms) => (
          <Tag
            style={{
              backgroundColor: "transparent",
              borderColor: this.getLatencyColor(ms || 0),
              color: this.getLatencyColor(ms || 0),
              fontWeight: 700,
              fontSize: "13px",
              padding: "2px 8px",
            }}
          >
            {ms !== undefined ? `${ms} ms` : "-"}
          </Tag>
        ),
      },
      {
        title: "Total Time in DB",
        dataIndex: "totalTimeMs",
        key: "totalTimeMs",
        width: 130,
        align: "right",
        sorter: (a, b) => (a.totalTimeMs || 0) - (b.totalTimeMs || 0),
        render: (ms, record) => {
          const sec = ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
          const pct = record.pressurePercent || 0;
          return (
            <div>
              <span style={{ fontWeight: 700, color: pct >= 15 ? "#dc2626" : "#0f172a" }}>
                {sec}
              </span>
              {pct > 0 && (
                <div style={{ fontSize: "11px", color: pct >= 15 ? "#dc2626" : "#64748b" }}>
                  {pct}% of DB time
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: "Action / Redis Advice",
        dataIndex: "redisRecommendation",
        key: "redisRecommendation",
        width: 180,
        render: (rec) => {
          if (!rec) return <span style={{ color: "#94a3b8" }}>-</span>;
          const colorMap = {
            danger: "red",
            primary: "blue",
            warning: "orange",
            info: "purple",
            default: "default",
          };
          return (
            <Tooltip title={rec.tip}>
              <Tag
                color={colorMap[rec.level] || "default"}
                style={{ fontWeight: 600, cursor: "help", fontSize: "12px", padding: "2px 8px" }}
              >
                {rec.badge}
              </Tag>
            </Tooltip>
          );
        },
      },
    ];

    return (
      <div className="query-performance-container" style={{ marginTop: 15 }}>
        {/* Header & Controls */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h4 style={{ margin: 0, fontWeight: 700, color: "#0f172a" }}>
                Query & API Performance Metrics
              </h4>
              {refreshing && (
                <Tag color="processing" icon={<SyncOutlined spin />}>
                  Refreshing...
                </Tag>
              )}
            </div>
            <p style={{ margin: "3px 0 0", color: "#64748b", fontSize: "13px" }}>
              Real-time query execution count, latency, and database workload.
              {lastUpdated && (
                <span style={{ marginLeft: 8, color: "#94a3b8" }}>
                  (Updated: {lastUpdated.toLocaleTimeString()})
                </span>
              )}
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <Select
              value={autoRefreshSec}
              onChange={this.handleAutoRefreshChange}
              style={{ width: 130 }}
              size="middle"
            >
              <Option value={0}>Auto: Off</Option>
              <Option value={5}>Every 5s</Option>
              <Option value={10}>Every 10s</Option>
              <Option value={30}>Every 30s</Option>
            </Select>

            <Button
              icon={<ReloadOutlined spin={loading || refreshing} />}
              onClick={() => this.loadMetrics()}
              disabled={loading || refreshing}
            >
              Refresh
            </Button>

            <Tooltip title="View standalone HTML page">
              <Button
                icon={<LinkOutlined />}
                onClick={this.openStandaloneDashboard}
              >
                HTML View
              </Button>
            </Tooltip>

            <Popconfirm
              title="Reset query counters and statistics?"
              onConfirm={this.handleReset}
              okText="Yes, Reset"
              cancelText="Cancel"
              okButtonProps={{ danger: true, loading: resetting }}
            >
              <Button danger icon={<DeleteOutlined />} loading={resetting}>
                Reset
              </Button>
            </Popconfirm>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            message="Error Loading Metrics"
            description={error}
            type="error"
            showIcon
            closable
            action={
              <Button size="small" danger onClick={() => this.loadMetrics()}>
                Retry
              </Button>
            }
            style={{ marginBottom: 16 }}
          />
        )}

        {/* 4 Clean Key Performance Cards */}
        <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
          <Col xs={12} sm={6}>
            <Card bodyStyle={{ padding: "14px 16px" }} className="metric-kpi-card">
              <Statistic
                title="Total API Calls"
                value={system.total_requests ?? 0}
                valueStyle={{ color: "#0f172a", fontWeight: 700 }}
                prefix={<ApiOutlined style={{ color: "#2563eb" }} />}
              />
              <div style={{ fontSize: "11px", color: "#64748b", marginTop: 4 }}>
                Live: <b>{system.current_total_qps ?? 0}</b> req/s · Peak: <b>{system.peak_total_qps ?? 0}</b> req/s
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={6}>
            <Card bodyStyle={{ padding: "14px 16px" }} className="metric-kpi-card">
              <Statistic
                title="Total DB Queries"
                value={system.total_db_queries ?? 0}
                valueStyle={{ color: "#0f172a", fontWeight: 700 }}
                prefix={<DatabaseOutlined style={{ color: "#10b981" }} />}
              />
              <div style={{ fontSize: "11px", color: "#64748b", marginTop: 4 }}>
                Live: <b>{system.current_db_qps ?? 0}</b> /s · Peak: <b>{system.peak_db_qps ?? 0}</b> /s
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={6}>
            <Card bodyStyle={{ padding: "14px 16px" }} className="metric-kpi-card">
              <Statistic
                title="Avg DB Time Taken"
                value={system.global_avg_db_time_ms ?? 0}
                suffix="ms"
                valueStyle={{
                  color: this.getLatencyColor(system.global_avg_db_time_ms || 0),
                  fontWeight: 700,
                }}
                prefix={<ClockCircleOutlined />}
              />
              <div style={{ fontSize: "11px", color: "#64748b", marginTop: 4 }}>
                Avg API latency: <b>{system.global_avg_latency_ms ?? 0} ms</b>
              </div>
            </Card>
          </Col>

          <Col xs={12} sm={6}>
            <Card bodyStyle={{ padding: "14px 16px" }} className="metric-kpi-card">
              <Statistic
                title="Total DB Time"
                value={system.total_db_time_ms ? (system.total_db_time_ms / 1000).toFixed(1) : 0}
                suffix="s"
                valueStyle={{ color: "#475569", fontWeight: 700 }}
                prefix={<RocketOutlined style={{ color: "#f59e0b" }} />}
              />
              <div style={{ fontSize: "11px", color: "#64748b", marginTop: 4 }}>
                Uptime: <b>{system.uptime_human || "N/A"}</b> · PID: {system.process_id || "N/A"}
              </div>
            </Card>
          </Col>
        </Row>

        {/* View Toggle (Clean & Prominent) */}
        <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <Radio.Group
            value={activeView}
            onChange={(e) => this.setState({ activeView: e.target.value, searchQuery: "" })}
            size="middle"
            buttonStyle="solid"
          >
            <Radio.Button value="db">
              <DatabaseOutlined style={{ marginRight: 6 }} />
              Database (SQL) Queries ({(data && data.db_queries ? data.db_queries.length : 0)})
            </Radio.Button>
            <Radio.Button value="routes">
              <ApiOutlined style={{ marginRight: 6 }} />
              API Endpoints ({filteredRoutes.length})
            </Radio.Button>
          </Radio.Group>

          <span style={{ fontSize: "12px", color: "#64748b" }}>
            Showing <b>{activeView === "db" ? filteredDbQueries.length : filteredRoutes.length}</b> {activeView === "db" ? "SQL queries" : "endpoints"}
          </span>
        </div>

        {/* Search & Sort Bar */}
        <Card style={{ marginBottom: 14 }} bodyStyle={{ padding: "12px 14px" }}>
          <Row gutter={[10, 10]} align="middle">
            <Col xs={24} sm={12} md={12}>
              <Input
                placeholder={activeView === "db" ? "Search queries..." : "Search endpoints..."}
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                value={searchQuery}
                onChange={(e) => this.setState({ searchQuery: e.target.value })}
                allowClear
              />
            </Col>
            <Col xs={12} sm={6} md={6}>
              {activeView === "db" ? (
                <Select
                  value={opFilter}
                  onChange={(val) => this.setState({ opFilter: val })}
                  style={{ width: "100%" }}
                >
                  <Option value="ALL">All Operations</Option>
                  <Option value="SELECT">SELECT (Reads)</Option>
                  <Option value="INSERT">INSERT (Writes)</Option>
                  <Option value="UPDATE">UPDATE (Mutations)</Option>
                  <Option value="DELETE">DELETE</Option>
                  <Option value="CALL">CALL (Stored Proc)</Option>
                </Select>
              ) : (
                <Select
                  value={methodFilter}
                  onChange={(val) => this.setState({ methodFilter: val })}
                  style={{ width: "100%" }}
                >
                  <Option value="ALL">All HTTP Methods</Option>
                  <Option value="GET">GET</Option>
                  <Option value="POST">POST</Option>
                  <Option value="PUT">PUT</Option>
                  <Option value="DELETE">DELETE</Option>
                </Select>
              )}
            </Col>
            <Col xs={12} sm={6} md={6}>
              <Select
                value={sortBy}
                onChange={this.handleSortChange}
                style={{ width: "100%" }}
              >
                <Option value="requests">Sort: Most Frequently Run</Option>
                <Option value="pressure">Sort: Heaviest DB Load</Option>
                <Option value="latency">Sort: Slowest Time Taken</Option>
                <Option value="qps">Sort: Highest Live QPS</Option>
              </Select>
            </Col>
          </Row>
        </Card>

        {/* Clean Results Table */}
        <Card bodyStyle={{ padding: 0 }}>
          {activeView === "db" ? (
            <Table
              columns={dbColumns}
              dataSource={filteredDbQueries}
              rowKey={(q) => q.key || `${q.operation}-${q.table}-${q.query}`}
              loading={loading}
              pagination={{
                defaultPageSize: 15,
                showSizeChanger: true,
                pageSizeOptions: ["10", "15", "25", "50"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} queries`,
              }}
              scroll={{ x: 950 }}
              size="middle"
            />
          ) : (
            <Table
              columns={routeColumns}
              dataSource={filteredRoutes}
              rowKey={(r) => r.key || `${r.method}-${r.route}`}
              loading={loading}
              pagination={{
                defaultPageSize: 15,
                showSizeChanger: true,
                pageSizeOptions: ["10", "15", "25", "50"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} endpoints`,
              }}
              scroll={{ x: 800 }}
              size="middle"
            />
          )}
        </Card>
      </div>
    );
  }
}

export default QueryPerformanceTab;
